import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Stack,
  Divider,
  Chip,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { searchAPI } from "../API/Seach";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { tokens } from "../theme/tokens";

const PENDING_BOOK_STORAGE_KEY = "tripTrip_pendingBookContext";
const THEME_PRIMARY = tokens.brand;
const THEME_ACCENT = tokens.accent;

/**
 * One POST /api/Hotel/itinerary/{code}/book per (itineraryCode, orderNumber) per session.
 * React 18 Strict Mode and effect re-runs otherwise fire two parallel book calls.
 */
const itineraryBookPromiseByKey = new Map();

function bookItineraryOnce(itineraryCode, orderNumber, bookPayload) {
  const key = `${String(itineraryCode)}::${String(orderNumber)}`;
  const existing = itineraryBookPromiseByKey.get(key);
  if (existing) {
    return existing;
  }
  const promise = searchAPI
    .bookItinerary(itineraryCode, bookPayload)
    .then((res) => {
      const bookOk =
        res?.status === "success" || Number(res?.statusCode) === 200;
      if (!bookOk) {
        itineraryBookPromiseByKey.delete(key);
      }
      return res;
    })
    .catch((err) => {
      itineraryBookPromiseByKey.delete(key);
      throw err;
    });
  itineraryBookPromiseByKey.set(key, promise);
  return promise;
}

function isPaymentCharged(data) {
  if (!data) return false;
  if (data.isSuccess === true) return true;
  const s = String(data.status || "").toUpperCase();
  if (s === "CHARGED" || s === "SUCCESS" || s === "CAPTURED" || s === "PAID") {
    return true;
  }
  const p = data.payment;
  if (p && typeof p === "object") {
    const ps = String(p.status || "").toUpperCase();
    if (ps === "SUCCESS" || ps === "CHARGED" || ps === "CAPTURED" || ps === "PAID") {
      return true;
    }
  }
  return false;
}

/** Flattens wallet / initiate-style payloads so the success card can read amount, status, etc. */
function normalizePaymentStatusForUi(data) {
  if (!data || typeof data !== "object") return data;
  const p = data.payment;
  return {
    ...data,
    status: data.status ?? p?.status,
    amount: data.amount ?? p?.amount,
    currency: data.currency ?? p?.currency,
    transactionId: data.transactionId ?? p?.transactionId,
    paymentDate: data.paymentDate ?? data.paymentExpiry ?? p?.paymentDate,
    paymentMethod:
      data.paymentMethod ??
      (String(data.paymentSessionId || "").toLowerCase() === "wallet"
        ? "Wallet"
        : undefined),
  };
}

function isPaymentPendingState(data) {
  if (!data) return true;
  if (isPaymentCharged(data)) return false;
  const s = String(data.status || "").toUpperCase();
  return (
    ["PENDING", "INITIATED", "PROCESSING", "AUTHORIZED", "NEW"].includes(s) &&
    data.isSuccess !== true
  );
}

function formatMoney(amount, currency = "INR") {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const cur = currency === "INR" ? "₹" : `${currency} `;
  return `${cur}${Number(amount).toLocaleString("en-IN")}`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: "0.7rem", pt: 0.15 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={600} textAlign="right" sx={{ wordBreak: "break-word", fontSize: "0.75rem" }}>
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

/** /payment-status?order_id=HTL-…&pgName=wallet — payment status API + book itinerary when charged */
function PaymentStatusUrlFlow({ orderRef, pgName }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [bookDone, setBookDone] = useState(false);
  const [bookError, setBookError] = useState(null);
  const [topError, setTopError] = useState(null);

  const runCheck = useCallback(async () => {
    setTopError(null);
    setBookError(null);
    setPhase("checking");
    try {
      const res = await searchAPI.getPaymentOrderStatus(orderRef, pgName);
      const ok =
        res?.status === "success" || Number(res?.statusCode) === 200;
      if (!ok) {
        setTopError(res?.message || "Could not load payment status.");
        setPhase("failed");
        return;
      }
      const data = res?.data;
      const uiData = normalizePaymentStatusForUi(data);
      setPaymentData(uiData);

      if (isPaymentCharged(data)) {
        setPhase("booking");
        await new Promise((r) => setTimeout(r, 80));
        let ctx = null;
        try {
          const raw = sessionStorage.getItem(PENDING_BOOK_STORAGE_KEY);
          ctx = raw ? JSON.parse(raw) : null;
        } catch {
          ctx = null;
        }

        if (ctx?.itineraryCode && ctx?.query && ctx.traceId) {
          const orderIdForBook = String(data.orderId || orderRef || "").trim();
          const bookPayload = {
            query: ctx.query,
            traceId: ctx.traceId,
            orderNumber: orderIdForBook,
            paymentReference: String(data.transactionId || ""),
            remarks: "Payment completed",
          };
          try {
            const bookRes = await bookItineraryOnce(
              ctx.itineraryCode,
              orderIdForBook,
              bookPayload
            );
            const bookOk =
              bookRes?.status === "success" ||
              Number(bookRes?.statusCode) === 200;
            if (bookOk) {
              setBookDone(true);
              try {
                sessionStorage.removeItem(PENDING_BOOK_STORAGE_KEY);
              } catch {
                /* ignore */
              }
            } else {
              setBookError(
                bookRes?.message ||
                  "Booking could not be confirmed automatically."
              );
              // toast.warning(
              //   bookRes?.message ||
              //     "Payment received, but booking confirmation failed."
              // );
            }
          } catch (err) {
            console.error(err);
            const msg =
              err?.response?.data?.message ||
              err?.message ||
              "Booking request failed.";
            setBookError(msg);
            // toast.warning(
            //   "Payment received, but we could not confirm the reservation. Please contact support."
            // );
          }
        } else {
          // toast.info(
          //   "Payment verified. If your booking is not updated, return to the app from the same browser session or contact support."
          // );
        }
        setPhase("success");
        return;
      }

      if (isPaymentPendingState(data)) {
        setPhase("pending");
        return;
      }

      setTopError(data?.message || res?.message || "Payment was not successful.");
      setPhase("failed");
    } catch (e) {
      console.error(e);
      setTopError(
        e?.response?.data?.message ||
          e?.message ||
          "Something went wrong while checking payment."
      );
      setPhase("failed");
    }
  }, [orderRef, pgName]);

  useEffect(() => {
    runCheck();
  }, [runCheck]);

  const renderBody = () => {
    if (phase === "checking") {
      return (
        <Stack spacing={1.75} alignItems="center" sx={{ py: 1 }}>
          <Box sx={{ position: "relative", width: "100%" }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: "rgba(27, 60, 115, 0.12)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 2,
                  bgcolor: THEME_ACCENT,
                },
              }}
            />
          </Box>
          <CircularProgress size={40} thickness={4} sx={{ color: THEME_PRIMARY }} />
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={800} color={THEME_PRIMARY} gutterBottom sx={{ fontSize: "1.1rem" }}>
              Checking payment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mx: "auto", fontSize: "0.8125rem" }}>
              We&apos;re verifying your transaction with the payment provider. This usually takes a
              few seconds.
            </Typography>
            <Chip
              size="small"
              label={orderRef}
              sx={{ mt: 1.25, fontWeight: 600, bgcolor: "rgba(0, 113, 194, 0.08)", color: THEME_ACCENT, maxWidth: "100%" }}
            />
          </Box>
        </Stack>
      );
    }

    if (phase === "booking") {
      return (
        <Stack spacing={1.5} alignItems="center" sx={{ py: 1.5 }}>
          <CircularProgress size={36} sx={{ color: THEME_ACCENT }} />
          <Typography variant="subtitle1" fontWeight={700} color={THEME_PRIMARY}>
            Confirming your booking…
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: "0.8125rem" }}>
            Payment succeeded. We&apos;re finalizing your reservation.
          </Typography>
        </Stack>
      );
    }

    if (phase === "success" && paymentData) {
      return (
        <Stack spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              bgcolor: "rgba(46, 125, 50, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 32, color: "success.main" }} />
          </Box>
          <Typography variant="h6" fontWeight={800} color="success.dark" textAlign="center" sx={{ fontSize: "1.1rem" }}>
            Payment successful
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: "0.8125rem" }}>
            {bookDone
              ? "Your booking has been confirmed."
              : bookError
              ? "Your payment was received. Booking confirmation may need a manual follow-up."
              : "Your payment was received successfully."}
          </Typography>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 1.5, sm: 1.75 },
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: tokens.surface,
              textAlign: "left",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
              <ReceiptLongOutlinedIcon sx={{ color: THEME_ACCENT, fontSize: 20 }} />
              <Typography variant="caption" fontWeight={700} color={THEME_PRIMARY} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                Transaction details
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1.25 }} />
            <Stack spacing={0.75}>
              <DetailRow label="Order" value={paymentData.orderId || orderRef} />
              <DetailRow label="Status" value={paymentData.status} />
              <DetailRow label="Amount" value={formatMoney(paymentData.amount, paymentData.currency)} />
              {paymentData.effectiveAmount != null &&
                Number(paymentData.effectiveAmount) !== Number(paymentData.amount) && (
                  <DetailRow
                    label="Effective amount"
                    value={formatMoney(paymentData.effectiveAmount, paymentData.currency)}
                  />
                )}
              <DetailRow label="Method" value={paymentData.paymentMethod || "—"} />
              <DetailRow label="Transaction ID" value={paymentData.transactionId || "—"} />
              <DetailRow label="Paid on" value={formatDateTime(paymentData.paymentDate)} />
            </Stack>
          </Paper>

          {bookError && (
            <Typography variant="caption" color="warning.dark" textAlign="center" display="block">
              {bookError}
            </Typography>
          )}

          <Button
            variant="contained"
            size="medium"
            onClick={() => navigate("/")}
            fullWidth
            sx={{
              mt: 0.5,
              textTransform: "none",
              fontWeight: 700,
              py: 1,
              bgcolor: THEME_PRIMARY,
              "&:hover": { bgcolor: tokens.brandDeep },
            }}
          >
            Go to home
          </Button>
        </Stack>
      );
    }

    if (phase === "pending") {
      return (
        <Stack spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <PendingIcon sx={{ fontSize: 44, color: "warning.main" }} />
          <Typography variant="h6" fontWeight={800} color="warning.dark" textAlign="center" sx={{ fontSize: "1.1rem" }}>
            Payment pending
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: "0.8125rem" }}>
            We haven&apos;t received a final confirmation yet. You can refresh to check again.
          </Typography>
          <Chip label={orderRef} size="small" sx={{ fontWeight: 600 }} />
          <Button variant="contained" onClick={() => runCheck()} sx={{ textTransform: "none", fontWeight: 700, bgcolor: THEME_PRIMARY }}>
            Check again
          </Button>
          <Button variant="text" onClick={() => navigate("/")} sx={{ textTransform: "none" }}>
            Go to home
          </Button>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5} alignItems="center" sx={{ py: 1 }}>
        <ErrorIcon sx={{ fontSize: 44, color: "error.main" }} />
        <Typography variant="h6" fontWeight={800} color="error.dark" textAlign="center" sx={{ fontSize: "1.1rem" }}>
          Unable to verify payment
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: "0.8125rem" }}>
          {topError || "Something went wrong."}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%", mt: 1 }}>
          <Button fullWidth variant="contained" onClick={() => runCheck()} sx={{ textTransform: "none", fontWeight: 700, bgcolor: THEME_PRIMARY }}>
            Try again
          </Button>
          <Button fullWidth variant="outlined" onClick={() => navigate("/")} sx={{ textTransform: "none", fontWeight: 600 }}>
            Go to home
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <Box
        sx={{
          minHeight: "100dvh",
          mt: 2,
          px: { xs: 1.5, sm: 2 },
          py: { xs: 2, sm: 2.5 },
          background: `linear-gradient(180deg, ${tokens.surfaceAlt} 0%, ${tokens.paper} 45%, ${tokens.surface} 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            mt: { xs: 2.5, sm: 3, md: 3.5 },
            maxWidth: { xs: "100%", sm: 440 },
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 440,
              mx: "auto",
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 8px 28px rgba(27, 60, 115, 0.08)",
              overflow: "hidden",
              position: "relative",
              maxHeight: { xs: "calc(100dvh - 120px)", sm: "calc(100dvh - 140px)" },
              display: "flex",
              flexDirection: "column",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${THEME_PRIMARY}, ${THEME_ACCENT})`,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: THEME_ACCENT,
                fontWeight: 700,
                letterSpacing: 0.8,
                fontSize: "0.68rem",
                flexShrink: 0,
              }}
            >
              TripTrip — Payment
            </Typography>
            <Box sx={{ overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0, pt: 0.5 }}>
              {renderBody()}
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}

/** When false, legacy flow does not call confirmPayment. */
const PAYMENT_STATUS_API_ENABLED = false;

function LegacyPaymentStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingOrder, paymentMethod } = location.state || {};
  const [timeLeft, setTimeLeft] = useState(180);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const paymentLinkOpened = useRef(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  let orderId = bookingOrder?.bookingId;
  let pgName = bookingOrder?.pgName;

  useEffect(() => {
    if (orderId) {
      localStorage.setItem("orderId", orderId);
    }
    if (pgName) {
      localStorage.setItem("pgName", pgName);
    }
  }, [orderId, pgName]);

  const checkPhonePeSDK = () => {
    return window.PhonePeCheckout && typeof window.PhonePeCheckout.transact === "function";
  };

  const waitForSDK = (maxAttempts = 10, interval = 1000) => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const checkSDK = () => {
        attempts++;
        if (checkPhonePeSDK()) {
          setSdkLoaded(true);
          resolve(true);
        } else if (attempts >= maxAttempts) {
          setSdkLoaded(false);
          reject(new Error("PhonePe SDK not available"));
        } else {
          setTimeout(checkSDK, interval);
        }
      };
      checkSDK();
    });
  };

  useEffect(() => {
    const checkSDKOnMount = async () => {
      try {
        await waitForSDK(5, 1000);
      } catch {
        /* ignore */
      }
    };
    checkSDKOnMount();
  }, []);

  const checkPaymentStatus = async () => {
    if (!PAYMENT_STATUS_API_ENABLED) {
      return;
    }
    try {
      const currentPgName = localStorage.getItem("pgName");
      const currentOrderId = localStorage.getItem("orderId");
      if (currentOrderId) {
        const response = await searchAPI.confirmPayment(currentOrderId, currentPgName);
        if (response?.status && response?.code === 200) {
          setPaymentStatus(response.data.status);
          setTransactionDetails(response.data);
        }
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.origin.includes("phonepe.com") || event.origin.includes("mercury")) {
        if (typeof event.data === "string") {
          if (event.data.includes("success") || event.data.includes("SUCCESS")) {
            setPaymentStatus("SUCCESS");
            setTimeout(() => {
              checkPaymentStatus();
            }, 3000);
            return;
          }
          if (event.data.includes("cancel") || event.data.includes("CANCEL")) {
            setPaymentStatus("FAILED");
            setTransactionDetails((prev) => ({
              ...prev,
              status: "FAILED",
              message: "Payment cancelled by user",
            }));
            return;
          }
          if (event.data.includes("fail") || event.data.includes("FAIL")) {
            setPaymentStatus("FAILED");
            setTransactionDetails((prev) => ({
              ...prev,
              status: "FAILED",
              message: "Payment failed",
            }));
          }
        } else if (typeof event.data === "object") {
          if (event.data.status === "SUCCESS" || event.data.result === "SUCCESS") {
            setPaymentStatus("SUCCESS");
            setTimeout(() => {
              checkPaymentStatus();
            }, 3000);
          } else if (event.data.status === "FAILED" || event.data.result === "FAILED") {
            setPaymentStatus("FAILED");
            setTransactionDetails((prev) => ({
              ...prev,
              status: "FAILED",
              message: event.data.message || "Payment failed",
            }));
          } else if (event.data.status === "CANCELLED" || event.data.result === "CANCELLED") {
            setPaymentStatus("FAILED");
            setTransactionDetails((prev) => ({
              ...prev,
              status: "FAILED",
              message: "Payment cancelled by user",
            }));
          }
        }
      }
    };
    window.addEventListener("message", handleIframeMessage);
    return () => window.removeEventListener("message", handleIframeMessage);
  }, []);

  const openPhonePeIframe = async () => {
    try {
      if (!bookingOrder?.paymentLink) {
        return;
      }
      await waitForSDK();
      if (checkPhonePeSDK()) {
        setSdkLoaded(true);
        window.PhonePeCheckout.transact({
          tokenUrl: bookingOrder.paymentLink,
          type: "IFRAME",
          callback: (response) => {
            if (response === "USER_CANCEL") {
              setPaymentStatus("FAILED");
              setTransactionDetails((prev) => ({
                ...prev,
                status: "FAILED",
                message: "User cancelled payment",
              }));
            } else if (response === "CONCLUDED") {
              checkPaymentStatus();
            }
          },
        });
        window.opener?.close();
      }
    } catch (error) {
      console.error("PhonePe SDK:", error);
    }
  };

  useEffect(() => {
    const handlePaymentComplete = (event) => {
      if (event.data && event.data.type === "HDFC_PAYMENT_COMPLETE") {
        navigate(`/payment-status?order_id=${encodeURIComponent(event.data.orderId || "")}`);
      }
    };
    window.addEventListener("message", handlePaymentComplete);
    return () => window.removeEventListener("message", handlePaymentComplete);
  }, [navigate]);

  useEffect(() => {
    if (window.opener) {
      const currentQueryParams = new URLSearchParams(window.location.search);
      const currentOrderId =
        currentQueryParams.get("order_id") || currentQueryParams.get("orderId");
      window.opener.postMessage(
        { type: "HDFC_PAYMENT_COMPLETE", orderId: currentOrderId },
        window.location.origin
      );
      window.close();
    }

    if (paymentMethod === "HDFC" && bookingOrder?.paymentLink && !paymentLinkOpened.current) {
      window.open(bookingOrder.paymentLink, "_blank");
      paymentLinkOpened.current = true;
    } else if (paymentMethod === "PHONEPE" && bookingOrder?.paymentLink) {
      openPhonePeIframe().catch(() => {});
    }

    const initialCheckTimeout = setTimeout(checkPaymentStatus, 3000);
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setPaymentStatus("FAILED");
          setTransactionDetails((prevDetails) => ({
            ...prevDetails,
            status: "FAILED",
            message: "Payment time expired",
          }));
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => {
      clearInterval(timer);
      clearTimeout(initialCheckTimeout);
    };
  }, [navigate, bookingOrder, paymentMethod]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleGoToHome = () => {
    navigate("/");
  };

  const renderPaymentStatus = () => {
    switch (paymentStatus) {
      case "SUCCESS":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: "success.main" }} />
            <Typography variant="h4" gutterBottom sx={{ color: "success.main", fontWeight: "bold" }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your booking has been confirmed
            </Typography>
            {transactionDetails && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(0, 0, 0, 0.04)", borderRadius: 2, width: "100%" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Order ID:</strong> {transactionDetails?.tripTripOrderId || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Transaction ID:</strong> {transactionDetails?.transactionId || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Amount:</strong> ₹{transactionDetails?.amount || "N/A"}
                </Typography>
              </Box>
            )}
            <Button variant="contained" onClick={handleGoToHome} sx={{ mt: 2 }}>
              Go to Home
            </Button>
          </Box>
        );
      case "PENDING":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <PendingIcon sx={{ fontSize: 60, color: "warning.main" }} />
            <Typography variant="h4" gutterBottom sx={{ color: "warning.main", fontWeight: "bold" }}>
              Payment Pending
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your payment is being processed
            </Typography>
          </Box>
        );
      case "FAILED":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <ErrorIcon sx={{ fontSize: 60, color: "error.main" }} />
            <Typography variant="h4" gutterBottom sx={{ color: "error.main", fontWeight: "bold" }}>
              Payment Failed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your payment could not be processed
            </Typography>
            <Button variant="contained" onClick={handleGoToHome} sx={{ mt: 2 }}>
              Try Again
            </Button>
          </Box>
        );
      default:
        return (
          <>
            <Typography variant="h4" gutterBottom sx={{ color: THEME_PRIMARY, fontWeight: "bold" }}>
              Payment in Progress
            </Typography>
            <Typography variant="h2" sx={{ my: 4, color: THEME_PRIMARY }}>
              {formatTime(timeLeft)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please complete your payment within the time limit
            </Typography>
            {bookingOrder?.paymentLink && paymentMethod !== "PHONEPE" && (
              <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                If the payment page didn&apos;t open automatically,{" "}
                <a href={bookingOrder.paymentLink} target="_blank" rel="noopener noreferrer" style={{ color: THEME_PRIMARY }}>
                  click here
                </a>
              </Typography>
            )}
            {paymentMethod === "PHONEPE" && (
              <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                PhonePe payment is being processed...
              </Typography>
            )}
          </>
        );
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
        mt: { xs: 2.5, sm: 3, md: 3.5 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Processing Payment...
            </Typography>
          </Box>
        ) : paymentMethod === "PHONEPE" && paymentStatus === null ? (
          <Box sx={{ textAlign: "center", minHeight: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
              {sdkLoaded ? "Processing your payment..." : "Loading payment gateway..."}
            </Typography>
            <CircularProgress size={40} />
            {!sdkLoaded && (
              <>
                <Typography variant="caption" sx={{ mt: 2, color: "text.secondary", display: "block" }}>
                  If payment doesn&apos;t start automatically, click the button below
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => openPhonePeIframe().catch(() => {})}
                  sx={{ mt: 2 }}
                >
                  Try Again
                </Button>
              </>
            )}
          </Box>
        ) : (
          renderPaymentStatus()
        )}
      </Paper>
    </Container>
  );
}

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const orderRef =
    searchParams.get("order_id") || searchParams.get("orderId") || "";
  const pgName =
    searchParams.get("pgName") || searchParams.get("pg_name") || "";

  if (orderRef) {
    return <PaymentStatusUrlFlow orderRef={orderRef} pgName={pgName} />;
  }

  return <LegacyPaymentStatus />;
}
